import React, { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { X, Camera, Check, Truck } from "@phosphor-icons/react";
import LivePlateScanner from "../LivePlateScanner";

/**
 * TruckArrivalModal — Stage A of the truck lifecycle.
 * Supervisor scans plate + picks 1 or 2 skips. Opens an at-station truck visit.
 * No tons yet — weighbridge captures those in stage B.
 */
export default function TruckArrivalModal({ exportOp, onClose, onSuccess }) {
    const [plate, setPlate] = useState("");
    const [skips, setSkips] = useState(1);
    const [scanning, setScanning] = useState(false);
    const [scanBusy, setScanBusy] = useState(false);
    const [busy, setBusy] = useState(false);
    const [transporter, setTransporter] = useState(null); // resolved via /fleet/transporters/lookup

    // Look up transporter whenever plate stabilises
    React.useEffect(() => {
        const p = plate.replace(/\s+/g, "");
        if (!p) { setTransporter(null); return; }
        const timer = setTimeout(async () => {
            try {
                const r = await api.get(`/fleet/transporters/lookup/${encodeURIComponent(p)}`);
                setTransporter(r.data?.found ? r.data.transporter : null);
            } catch { /* silent */ }
        }, 350);
        return () => clearTimeout(timer);
    }, [plate]);

    const submit = async (e) => {
        e.preventDefault();
        if (!plate.trim()) return toast.error("Truck registration is required — scan or type it.");
        setBusy(true);
        try {
            await api.post(`/tronox/exports/${exportOp.id}/truck-arrive`, {
                plate: plate.trim().toUpperCase(),
                skips,
            });
            toast.success(`Truck ${plate.trim().toUpperCase()} at station · ${skips} skip${skips === 1 ? "" : "s"}`);
            onSuccess();
        } catch (e2) { toast.error(formatApiError(e2)); }
        setBusy(false);
    };

    const onPlateCaptured = async (base64) => {
        setScanning(false);
        setScanBusy(true);
        try {
            const r = await api.post("/tronox/plate-scan", { image_base64: base64, mime_type: "image/jpeg" });
            if (r.data?.ok && r.data.plate) {
                setPlate(r.data.plate);
                toast.success(`Plate read in ${(r.data.elapsed_ms || 0) / 1000}s: ${r.data.plate}`);
            } else {
                toast.error(r.data?.reason || "Plate could not be read.");
            }
        } catch (e) { toast.error(formatApiError(e)); }
        setScanBusy(false);
    };

    if (scanning) {
        return <LivePlateScanner onCapture={onPlateCaptured} onCancel={() => setScanning(false)} />;
    }

    return (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3" data-testid="truck-arrival-modal">
            <div className="bg-slate-900 text-slate-100 rounded-xl w-full max-w-md border border-slate-700 shadow-2xl">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center">
                            <Truck size={18} weight="fill" className="text-emerald-300" />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-slate-400">Load-Out Station · Stage A</div>
                            <div className="text-lg font-black">Truck In</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="close-truck-arrival">
                        <X size={20} />
                    </button>
                </div>
                <form onSubmit={submit} className="p-4 space-y-4">
                    <div>
                        <label className="text-[11px] uppercase text-slate-400 tracking-wider">Truck registration</label>
                        <div className="flex gap-2 mt-1">
                            <input
                                value={plate}
                                onChange={(e) => setPlate(e.target.value.toUpperCase())}
                                placeholder="e.g. ND 234 GP"
                                className="g-input flex-1 font-mono text-lg tracking-wider"
                                data-testid="arrival-plate-input"
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={() => setScanning(true)}
                                disabled={scanBusy}
                                className="inline-flex items-center gap-1 px-3 rounded bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white text-sm font-bold"
                                data-testid="arrival-scan-plate"
                            >
                                <Camera size={16} weight="bold" /> {scanBusy ? "Reading…" : "Scan"}
                            </button>
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1">Snap once — the plate is read in ~1 second.</div>
                        {plate && (
                            transporter ? (
                                <div className="mt-2 rounded-lg border border-emerald-500/50 bg-emerald-500/10 p-2 text-[11px]" data-testid="arrival-transporter-detected">
                                    <div className="text-emerald-300 font-bold uppercase tracking-wider text-[10px]">Transporter detected</div>
                                    <div className="font-black text-white text-sm">{transporter.company_name}</div>
                                    {transporter.emergency_contact_name && <div className="text-slate-300">Emergency: {transporter.emergency_contact_name} · {transporter.emergency_contact_phone || "—"}</div>}
                                </div>
                            ) : (
                                <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-200" data-testid="arrival-transporter-unknown">
                                    Plate not registered to any transporter. Register it under <b>Fleet → Transporters</b> so future scans auto-recognise it.
                                </div>
                            )
                        )}
                    </div>
                    <div>
                        <label className="text-[11px] uppercase text-slate-400 tracking-wider">Skips on this truck</label>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                            {[1, 2].map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    onClick={() => setSkips(n)}
                                    className={`py-3 rounded font-black text-xl transition-colors ${skips === n ? "bg-teal-600 text-white ring-2 ring-teal-300" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}
                                    data-testid={`arrival-skips-${n}`}
                                >
                                    {n} skip{n === 1 ? "" : "s"}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-3 text-[11px] text-slate-400">
                        <b className="text-teal-300">Next step</b>: after the skips are filled at the station, the truck goes to the weighbridge — tap <b className="text-slate-200">Weighed Out</b> on the truck&apos;s row to capture the weighbridge tonnage and close the visit.
                    </div>
                    <div className="flex gap-2 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold"
                            data-testid="arrival-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={busy}
                            className="flex-1 inline-flex items-center justify-center gap-2 py-2 rounded bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white font-black"
                            data-testid="arrival-submit"
                        >
                            <Check size={16} weight="bold" /> {busy ? "Saving…" : "Truck In"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
